var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useState, useMemo, useCallback, useEffect } from "react";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import { debounce } from "lodash";
import { fetchCollections, fetchCollectionById, } from "../../api/collectionsService";
import { formatCollectionOptionLabel } from "../../utils/collectionDisplayUtils";
var toOption = function (collection, formatLabel) { return ({
    value: collection.id || "",
    label: formatLabel(collection),
}); };
var isValidCollection = function (c) {
    return !!c && !!c.id && !!c.mainName;
};
var toValidOptions = function (collections, formatLabel) {
    return collections
        .filter(isValidCollection)
        .map(function (collection) { return toOption(collection, formatLabel); });
};
export var useCollectionHandlers = function (params) {
    var okapiKy = useOkapiKy();
    var formatMessage = useIntl().formatMessage;
    var initialValues = params.initialValues, setValue = params.setValue;
    // DB-787: "Name (qualifier)", ceased editions marked with their end year
    var formatLabel = useCallback(function (collection) {
        return formatCollectionOptionLabel(collection, formatMessage);
    }, [formatMessage]);
    var _a = useState(""), filterName = _a[0], setFilterName = _a[1];
    var _b = useState([]), manualOptions = _b[0], setManualOptions = _b[1];
    var collectionIds = useMemo(function () {
        var _a;
        return ((_a = initialValues.titleCollections) === null || _a === void 0 ? void 0 : _a.map(function (tc) { var _a; return (_a = tc.collection) === null || _a === void 0 ? void 0 : _a.id; }).filter(function (id) { return !!id; })) || [];
    }, [initialValues.titleCollections]);
    useQuery(["titleCollections", "hydrate", collectionIds], function () {
        return Promise.all(collectionIds.map(function (id) { return fetchCollectionById(okapiKy, id); }));
    }, {
        enabled: collectionIds.length > 0,
        staleTime: Infinity,
        onSuccess: function (collections) {
            // editions the title already has stay offered even when ceased
            setManualOptions(toValidOptions(collections, formatLabel));
            collections.forEach(function (collection, index) {
                if (collection === null || collection === void 0 ? void 0 : collection.id) {
                    setValue("titleCollections.".concat(index, ".collection"), collection, {
                        shouldDirty: false,
                        shouldTouch: false,
                    });
                }
            });
        },
    });
    var isFiltering = filterName.trim().length > 0;
    var _c = useQuery(["collectionSearchOptions", filterName], function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a, items;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, fetchCollections(okapiKy, {
                        name: filterName,
                        excludeCeased: true,
                        offset: 0,
                        limit: 50,
                    })];
                case 1:
                    _a = (_b.sent()).items, items = _a === void 0 ? [] : _a;
                    return [2 /*return*/, items];
            }
        });
    }); }, { enabled: isFiltering }).data, filteredCollections = _c === void 0 ? [] : _c;
    var collectionOptions = useMemo(function () {
        return isFiltering
            ? toValidOptions(filteredCollections, formatLabel)
            : manualOptions;
    }, [isFiltering, filteredCollections, manualOptions, formatLabel]);
    var debouncedSetFilter = useMemo(function () { return debounce(function (name) { return setFilterName(name); }, 300); }, []);
    useEffect(function () {
        return function () {
            debouncedSetFilter.cancel();
        };
    }, [debouncedSetFilter]);
    var onCollectionFilter = useCallback(function (filterValue) {
        debouncedSetFilter(filterValue);
    }, [debouncedSetFilter]);
    var addCollectionOption = useCallback(function (collection) {
        if (!isValidCollection(collection)) {
            return;
        }
        debouncedSetFilter.cancel();
        setFilterName("");
        setManualOptions(function (prev) { return __spreadArray([
            toOption(collection, formatLabel)
        ], prev.filter(function (option) { return option.value !== collection.id; }), true); });
    }, [debouncedSetFilter, formatLabel]);
    var resetCollectionOptions = useCallback(function () {
        debouncedSetFilter.cancel();
        setFilterName("");
        setManualOptions([]);
    }, [debouncedSetFilter]);
    return {
        collectionOptions: collectionOptions,
        addCollectionOption: addCollectionOption,
        resetCollectionOptions: resetCollectionOptions,
        onCollectionFilter: onCollectionFilter,
    };
};
